import {
    Box,
    Button,
    Input,
    Modal,
    ModalBody,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    useDisclosure
} from '@chakra-ui/react';
import { t } from '@lingui/macro';
import React, {
    useMemo, useState
} from 'react';
import { useDispatch } from 'react-redux';

import iconDuplicateEmail from '../../../../../assets/icons/icon_duplicate_email.svg';
import iconMustVerifyEmail from '../../../../../assets/icons/icon_must_verify_email.svg';
import iconVerifiedDuplicateEmail from '../../../../../assets/icons/icon_verified_duplicate_email.svg';
import iconVerifyEmail from '../../../../../assets/icons/icon_verify_email.svg';
import { UserType } from '../../user-profile';
import { CheckMailVerificationStatusProps } from '../check-mail-verification-status/check-mail-verification-status';
import { verifyEmailAction } from '../check-mail-verification-status/check-mail-verification-status.actions';
import { ErrorMessage } from '../error-message/error-message';
import {
    VerifyEmailHeaderText,
    VerifyEmailLogo,
    VerifyEmailBodyText,
    ContactUsText,
    TextFieldWrapper,
    VerifyEmailButton,
    DoItLaterButton, ContactUsLink
} from './email-verification-modal.styled';

export type EmailVerificationModalProps = {
    isModalOpen: boolean
    isError: boolean
    userDetails: UserType | null
    errorCode: number | null
};

const getModalText = (props: CheckMailVerificationStatusProps) => {
    const {
        isVerifyEmailTimeframeExpired, isFirstLogin, emailStatus
    } = props;

    if ((isVerifyEmailTimeframeExpired && isFirstLogin) || !isVerifyEmailTimeframeExpired) {
        if (emailStatus === 'available') {
            return {
                headerText: t`emailVerificationModal.withinTimeFrame.headerText`,
                bodyText: t`emailVerificationModal.withinTimeFrame.bodyText`,
                helperText: t`emailVerificationModal.helperText`,
                imageAltName: t`emailVerificationModal.withinTimeFrame.imageAltName`,
                imageSrc: iconVerifyEmail,
                isCloseButtonVisible: true
            };
        } else {
            return {
                headerText: t`emailVerificationModal.duplicateEmail.headerText`,
                bodyText: t`emailVerificationModal.duplicateEmail.bodyText`,
                helperText: t`emailVerificationModal.helperText`,
                imageAltName: t`emailVerificationModal.duplicateEmail.imageAltName`,
                imageSrc: iconDuplicateEmail,
                isCloseButtonVisible: true
            };
        }
    } else {
        if (emailStatus === 'taken') {
            return {
                headerText: t`emailVerificationModal.timeFrameLapsedDuplicateEmail.headerText`,
                bodyText: t`emailVerificationModal.timeFrameLapsedDuplicateEmail.bodyText`,
                helperText: t`emailVerificationModal.helperText`,
                imageAltName: t`emailVerificationModal.timeFrameLapsedDuplicateEmail.imageAltName`,
                imageSrc: iconVerifiedDuplicateEmail,
                isCloseButtonVisible: false
            };
        } else {
            return {
                headerText: t`emailVerificationModal.timeFrameLapsed.headerText`,
                bodyText: t`emailVerificationModal.timeFrameLapsed.bodyText`,
                helperText: t`emailVerificationModal.helperText`,
                imageAltName: t`emailVerificationModal.timeFrameLapsed.imageAltName`,
                imageSrc: iconMustVerifyEmail,
                isCloseButtonVisible: false
            };
        }
    }
};

export const EmailVerificationModal = ({
    isModalOpen,
    isError,
    errorCode,
    userDetails
}: EmailVerificationModalProps) => {
    const { onOpen } = useDisclosure();
    const dispatch = useDispatch();
    const [isOpen, setIsOpen] = useState<boolean>(isModalOpen);
    const onSendVerifyEmail = (userProfileId: string) => {
        dispatch(verifyEmailAction(userProfileId));
        setIsOpen(false);
    };
    const onClose = () => {
        setIsOpen(false);
    };
    const {
        helperText,
        bodyText,
        headerText,
        imageSrc,
        imageAltName,
        isCloseButtonVisible
    } = useMemo(() => getModalText({
        isVerifyEmailTimeframeExpired: userDetails?.isVerifyEmailTimeframeExpired || null,
        isFirstLogin: userDetails?.isFirstLogin || null,
        emailStatus: userDetails?.emailStatus || null
    }), [userDetails?.emailStatus, userDetails?.isFirstLogin, userDetails?.isVerifyEmailTimeframeExpired]);

    return (
        <>
            {/*this button is just to re-open email-verification-modal in storybook
            if its closed. will be deleted later on once BE is ready*/}
            <Button data-testid={'open-email-verification-modal'} onClick={onOpen}>Open Modal</Button>

            <Modal blockScrollOnMount={false} isOpen={isOpen} onClose={onClose} closeOnEsc={false}>
                <ModalOverlay />
                <ModalContent maxWidth={'860px'} borderRadius={'20px'} boxShadow={'0px 2px 8px rgb(141 156 161 / 24%)'}>
                    <Box>
                        <ModalHeader pt={'54px'} pb={'8px'}>
                            <VerifyEmailLogo >
                                <img
                                    src={imageSrc}
                                    alt={imageAltName}
                                />
                            </VerifyEmailLogo>
                        </ModalHeader>

                        <ModalBody flex={0} pb={'0'}>
                            <VerifyEmailHeaderText data-testid={'verify-email-header'}>
                                {headerText}
                            </VerifyEmailHeaderText>
                            <TextFieldWrapper><Input h={'56px'} defaultValue={userDetails?.email} /></TextFieldWrapper>

                            { isError ?
                                <ErrorMessage errorCode={errorCode} />
                                : null
                            }

                            <VerifyEmailBodyText>
                                {bodyText}
                            </VerifyEmailBodyText>
                        </ModalBody>

                        <ModalFooter justifyContent={'center'} flexDirection={'column'} pb={'54px'}>
                            <Box fontSize={'18px'}>
                                <VerifyEmailButton
                                    data-testid={'lets-do-it-button'}
                                    onClick={() => onSendVerifyEmail(userDetails?.userProfileId || '')}
                                >
                                    {t`emailVerificationModal.confirmButton`}
                                </VerifyEmailButton>
                                { isCloseButtonVisible ?
                                    <DoItLaterButton
                                        data-testid={'do-it-later-button'}
                                        onClick={onClose}
                                    >
                                        {t`emailVerificationModal.closeModalButton`}
                                    </DoItLaterButton>: null
                                }
                            </Box>
                            <ContactUsText>
                                <span data-testid={'contact-us'}>{helperText}</span>
                                <ContactUsLink href={'https://www.3plearning.com/contact/'}>
                                    {t`Contact us`}
                                </ContactUsLink>
                            </ContactUsText>
                        </ModalFooter>
                    </Box>
                </ModalContent>
            </Modal>
        </>
    );
};
